import type { MonthView, PlanRowView } from '../api.js'
import {
  addMoney,
  esc,
  isLessThan,
  isPositive,
  money,
  money2,
  monthLong,
  shortDate,
  subtractMoney,
} from '../format.js'

/**
 * The weekly card charges panel.
 *
 * Wednesday charge → Received → Thursday payroll → Friday bills → Everyday →
 * Cushion after. Every figure here, including which deposits fall in which
 * week, is decided by the server; this only lays it out.
 */

const $ = <T extends HTMLElement>(selector: string): T =>
  document.querySelector<T>(selector) as T

export function renderPlan(view: MonthView, canWrite: boolean): void {
  const plan = view.plan
  const editable = canWrite && !view.closed
  const monthLabel = monthLong(view.month)
  const feeApplies = plan.cardFeePct !== '0.00'

  $('#chargeBox').innerHTML = `
    <span class="lbl">Charge each pay period (Wednesday)</span>
    <span class="big num">${money2(plan.weeklyCharge)}</span>
    <span class="sub num" style="font-size:13px;color:var(--ink)">${money(plan.monthlyAmount)} ÷ ${plan.payPeriods} pay period${plan.payPeriods > 1 ? 's' : ''}${
      plan.basis === 'recurring'
        ? ''
        : feeApplies
          ? ` = ${money2(plan.weeklyNet)}, plus ${plan.cardFeePct}% card fees`
          : ''
    }</span>
    ${
      plan.basis === 'recurring'
        ? `<span class="sub">Based on Recurring Monthly Revenue${
            plan.rmrSet
              ? ''
              : ' <b style="color:var(--warn)">(not set yet — enter it under Income sources)</b>'
          }. Additional revenue isn't planned on.</span>`
        : ''
    }
    <span class="sub">${
      feeApplies
        ? `About ${money2(plan.weeklyNet)} is received after ${plan.cardFeePct}% card fees. `
        : ''
    }Recurring received so far in ${esc(monthLabel.split(' ')[0] ?? '')}: <b>${money(plan.cardReceived)}</b>.</span>
    ${
      plan.payPeriodsSet
        ? ''
        : `<span class="sub" style="color:var(--warn)"><b>Pay periods for ${esc(
            monthLabel.split(' ')[0] ?? '',
          )} aren't set yet.</b> Using ${plan.payPeriods} for now. Enter 4 or 5 to set it.</span>`
    }`

  // Never overwrite a field the user is currently typing into.
  const active = document.activeElement
  const setValue = (selector: string, value: string) => {
    const element = $<HTMLInputElement | HTMLSelectElement>(selector)
    if (element !== active) element.value = value
  }

  $('#l-periods').textContent = `Pay periods in ${monthLabel.split(' ')[0] ?? ''}`
  $<HTMLInputElement>('#p-periods').placeholder = String(plan.wednesdayCount)
  setValue('#p-periods', plan.payPeriodsSet ? String(plan.payPeriods) : '')
  setValue('#p-basis', plan.basis === 'budget_payroll' ? 'budget' : plan.basis)
  setValue('#p-custom', plan.basis === 'custom' ? plan.monthlyAmount : '')
  setValue('#p-fee', plan.cardFeePct === '0.00' ? '' : plan.cardFeePct)
  setValue('#p-res', plan.cushionStart === '0.00' ? '' : plan.cushionStart)

  // Bill reminder settings.
  setValue('#p-alerts', view.alert.enabled ? 'on' : 'off')
  setValue('#p-alertdays', String(view.alert.leadDays))
  setValue('#p-alertday', String(view.alert.weekday))

  $('#w-custom').hidden = plan.basis !== 'custom'
  for (const selector of [
    '#p-periods',
    '#p-basis',
    '#p-custom',
    '#p-fee',
    '#p-res',
    '#p-alerts',
    '#p-alertdays',
    '#p-alertday',
  ]) {
    $<HTMLInputElement>(selector).disabled = !editable
  }

  $('#planExplain').textContent =
    `Amount per pay period = ${
      plan.basis === 'custom'
        ? 'your monthly amount'
        : plan.basis === 'recurring'
          ? 'Recurring Monthly Revenue'
          : "this month's category budgets + payroll"
    } ÷ the pay periods you set for ${monthLabel.split(' ')[0] ?? ''}. Additional revenue is added to the ` +
    `cushion only once it's in the bank. Each month keeps its own number. Received = recurring ` +
    `card deposits that have reached the NI bank account and are available. Recurring ACH and ` +
    `check payments count in the monthly totals at the top and in the cushion once they clear. ` +
    `Record each deposit on the day it's available, not the day it was charged or mailed.`

  const arrow = '<span class="arrow" aria-hidden="true">→</span>'

  $('#weeks').innerHTML =
    `<thead><tr>` +
    `<th class="wkhead">Wednesday Charge${arrow}</th>` +
    `<th class="r wkhead">Received${arrow}</th>` +
    `<th class="wkhead">Thursday Payroll${arrow}</th>` +
    `<th class="wkhead">Friday Bills${arrow}</th>` +
    `<th class="r wkhead">Everyday${arrow}</th>` +
    `<th class="r wkhead">Cushion After</th>` +
    `</tr></thead><tbody>${plan.rows.map((row) => renderRow(row, plan, editable)).join('')}</tbody>` +
    renderFoot(plan)
}

function renderRow(
  row: PlanRowView,
  plan: MonthView['plan'],
  editable: boolean,
): string {
  const receivedCell = isPositive(row.received)
    ? `<span class="num">${money2(row.received)}</span><div class="what ${
        isLessThan(row.received, plan.weeklyNet) ? 'neg' : ''
      }">${
        isLessThan(row.received, plan.weeklyNet)
          ? `${money2(subtractMoney(plan.weeklyNet, row.received))} under`
          : `expected ${money(plan.weeklyNet)}`
      }</div>`
    : `<b style="color:var(--warn)">Not in bank yet</b>${
        editable
          ? `<div><button class="btn small rec-btn" data-deposit="${row.thursday}">+ Record deposit</button></div>`
          : ''
      }`

  const extras =
    (isPositive(row.otherRecurring)
      ? `<div class="what" style="color:var(--good)">+ ${money2(row.otherRecurring)} recurring by ACH/check</div>`
      : '') +
    (isPositive(row.additional)
      ? `<div class="what" style="color:var(--good)">+ ${money2(row.additional)} additional → cushion</div>`
      : '')

  const names = (entries: Array<{ name: string; autopay?: boolean }>) =>
    entries.map((entry) => esc(entry.name) + (entry.autopay ? ' (auto)' : '')).join(', ')

  return `<tr>
    <td><b>${shortDate(row.wednesday)}</b><div class="what num">${money2(plan.weeklyCharge)} to charge</div></td>
    <td class="r">${receivedCell}${extras}</td>
    <td><span class="num">${isPositive(row.payroll) ? money2(row.payroll) : '—'}</span>${
      row.paydays.length
        ? `<div class="what">${shortDate(row.thursday)} · ${names(row.paydays)}</div>`
        : ''
    }</td>
    <td><span class="num">${isPositive(row.bills) ? money2(row.bills) : '—'}</span>${
      row.billsDue.length
        ? `<div class="what">${shortDate(row.friday)} · ${row.billsDue
            .map(
              (bill) =>
                `${esc(bill.name)}${bill.autopay ? ' (auto)' : ''} <span class="num">due ${shortDate(bill.date)}</span>`,
            )
            .join(', ')}</div>`
        : ''
    }</td>
    <td class="r num">${money2(row.everyday)}</td>
    <td class="r num ${row.short ? 'neg' : ''}">${money2(row.cushionAfter)}${
      row.short
        ? '<div class="what neg">short this week</div>'
        : row.projected
          ? '<div class="what">projected</div>'
          : ''
    }</td></tr>`
}

function renderFoot(plan: MonthView['plan']): string {
  const totalPayroll = addMoney(...plan.rows.map((row) => row.payroll))
  const totalBills = addMoney(...plan.rows.map((row) => row.bills))
  const totalEveryday = addMoney(...plan.rows.map((row) => row.everyday))
  const totalCharge = addMoney(...plan.rows.map(() => plan.weeklyCharge))
  const totalReceived = addMoney(...plan.rows.map((row) => row.received))

  return `<tfoot><tr>
    <td>${plan.payPeriods} pay periods · ${money(totalCharge)}</td>
    <td class="r num">${money2(totalReceived)}<div class="what">of ${money(plan.monthlyAmount)} expected</div></td>
    <td class="num">${money2(totalPayroll)}</td>
    <td class="num">${money2(totalBills)}</td>
    <td class="r num">${money2(totalEveryday)}</td>
    <td></td></tr></tfoot>`
}
